import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Please enter a new password');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/.test(password)) {
      setError('Password needs uppercase, lowercase, number & special character');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSubmitting(false);

    if (updateError) {
      setError(updateError.message);
    } else {
      setSuccess(true);
    }
  };

  if (success) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16 pb-12 bg-background-50">
          <div className="w-full max-w-md mx-auto px-4 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-6">
              <i className="ri-shield-check-line text-3xl text-accent-600"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">Password Reset!</h1>
            <p className="text-sm text-foreground-500 mb-6">Your password has been successfully updated.</p>
            <Link to="/login" className="px-8 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold text-base transition-all whitespace-nowrap inline-block cursor-pointer">
              Sign In
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen flex items-center justify-center pt-16 pb-12 bg-background-50">
        <div className="w-full max-w-md mx-auto px-4">
          <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8">
            <div className="text-center mb-8">
              <div className="w-12 h-12 mx-auto rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                <i className="ri-lock-password-line text-2xl text-primary-600"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950">Set New Password</h1>
              <p className="text-sm text-foreground-500 mt-1">Create a strong new password</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">{error}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="new-password">New Password</label>
                <input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors placeholder:text-foreground-400"
                  placeholder="Enter new password"
                  autoComplete="new-password"
                />
                <div className="mt-2 flex gap-1 flex-wrap">
                  {[
                    { test: password.length >= 8, label: '8+ chars' },
                    { test: /[A-Z]/.test(password), label: 'Uppercase' },
                    { test: /[a-z]/.test(password), label: 'Lowercase' },
                    { test: /\d/.test(password), label: 'Number' },
                    { test: /[!@#$%^&*]/.test(password), label: 'Special char' },
                  ].map((item) => (
                    <span
                      key={item.label}
                      className={`text-xs px-2 py-0.5 rounded ${
                        password
                          ? item.test
                            ? 'bg-accent-100 text-accent-700'
                            : 'bg-background-200 text-foreground-400'
                          : 'bg-background-100 text-foreground-400'
                      }`}
                    >
                      {item.label}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="confirm-new-password">Confirm Password</label>
                <input
                  id="confirm-new-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors placeholder:text-foreground-400"
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 disabled:cursor-not-allowed text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {submitting && <div className="w-4 h-4 rounded-full border-2 border-background-50 border-t-transparent animate-spin"></div>}
                {submitting ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}